import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { OwnMapOwnership, OwnMapSelection } from './ownMapTypes';
import { buildEmpireStats, formatCompactPop } from './empireStats';
import {
    territoryService,
    type CountyDetailResponse,
    type TeamEmpireTidbits,
} from '../../services/territoryService';
import './TerritoryDrawer.css';

type SoldierChip = { handle: string; tier: string; team_name?: string };

interface Props {
  leagueId: string;
  pick: OwnMapSelection;
  ownership: OwnMapOwnership | null;
  popByFips: Record<string, number | null>;
  countyLabels: Record<string, string>;
  selectedDate: string;
  selectedSeason?: number | null;
  soldiers?: SoldierChip[];
  onClose: () => void;
}

function pct(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0%';
  if (n < 0.01) return '<1%';
  return `${Math.round(n * 100)}%`;
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '';
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return d;
  }
}

/** NCAA basketball season_year 2008 → "2008-09"; otherwise just the year. */
function formatSeasonYear(leagueId: string, year: number | null | undefined): string {
  if (year == null || !Number.isFinite(year)) return '';
  if (leagueId.startsWith('ncaa_basketball') || leagueId.includes('basketball')) {
    const next = String(year + 1).slice(-2);
    return `${year}-${next}`;
  }
  return String(year);
}

function foeLabel(name: string | null | undefined, short: string | null | undefined): string {
  if (short && short.trim()) return short.trim();
  if (!name) return 'a rival';
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return name;
  // Drop trailing mascot word: "UConn Huskies" → "UConn"
  return parts.slice(0, -1).join(' ');
}

type ChainTeam = {
  teamId: number;
  name: string;
  short: string;
  color: string | null;
  logo_url?: string | null;
  espn_team_id?: number | null;
};

function teamLogoSrc(team: {
  logo_url?: string | null;
  espn_team_id?: number | null;
}): string | null {
  const espn = team.espn_team_id != null ? Math.abs(Number(team.espn_team_id)) : null;
  if (espn) return `/logos/ncaa/${espn}.png`;
  if (team.logo_url && /^https?:\/\//i.test(team.logo_url)) return team.logo_url;
  return null;
}

function shortMonthDay(d: string | null | undefined): string {
  if (!d) return '';
  try {
    return new Date(`${d}T12:00:00`)
      .toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      .toUpperCase();
  } catch {
    return d;
  }
}

function buildConquestChain(detail: CountyDetailResponse): {
  nodes: ChainTeam[];
  edges: { date: string | null; score: string | null }[];
} {
  const nodes: ChainTeam[] = [];
  const edges: { date: string | null; score: string | null }[] = [];

  const pushNode = (team: ChainTeam): boolean => {
    const last = nodes[nodes.length - 1];
    if (last && last.teamId === team.teamId) return false;
    nodes.push(team);
    return true;
  };

  if (detail.initial_owner) {
    pushNode({
      teamId: detail.initial_owner.team_id,
      name: detail.initial_owner.team_name,
      short: foeLabel(detail.initial_owner.team_name, detail.initial_owner.short_name),
      color: detail.initial_owner.map_color || detail.initial_owner.primary_color,
      logo_url: detail.initial_owner.logo_url,
      espn_team_id: detail.initial_owner.espn_team_id,
    });
  }

  for (const h of detail.history) {
    if (!detail.initial_owner && h.previous_owner_team_id != null && nodes.length === 0) {
      pushNode({
        teamId: h.previous_owner_team_id,
        name: h.previous_owner_name || `Team ${h.previous_owner_team_id}`,
        short: foeLabel(h.previous_owner_name, null),
        color: h.previous_owner_color || null,
        logo_url: h.previous_owner_logo,
        espn_team_id: h.previous_owner_espn_team_id,
      });
    }
    const added = pushNode({
      teamId: h.new_owner_team_id,
      name: h.new_owner_name || h.winner_name || `Team ${h.new_owner_team_id}`,
      short: foeLabel(h.new_owner_name || h.winner_name, null),
      color: h.new_owner_color || null,
      logo_url: h.new_owner_logo,
      espn_team_id: h.new_owner_espn_team_id,
    });
    if (added) {
      edges.push({
        date: h.game_date,
        score:
          h.winner_score != null && h.loser_score != null
            ? `${h.winner_score}–${h.loser_score}`
            : null,
      });
    }
  }

  return { nodes, edges };
}

const ChainLogo: React.FC<{ team: ChainTeam; current?: boolean }> = ({ team, current }) => {
  const src = teamLogoSrc(team);
  const [broken, setBroken] = useState(false);
  return (
    <span
      className={`om-drawer__chain-logo${current ? ' om-drawer__chain-logo--current' : ''}`}
      style={{ ['--chain-color' as string]: team.color || '#6c7179' }}
      title={team.name}
    >
      {src && !broken ? (
        <img src={src} alt="" onError={() => setBroken(true)} />
      ) : (
        <span className="om-drawer__chain-fallback" aria-hidden>
          {(team.name || '?').slice(0, 1)}
        </span>
      )}
    </span>
  );
};

function buildBrag(detail: CountyDetailResponse): string {
  const c = detail.county;
  const owner = detail.current_owner?.team_name || 'Unknown';
  const pop = c.population > 0 ? `${c.population.toLocaleString()} people` : 'land';
  const latest = detail.latest_conquest;
  if (latest?.loser_name && latest.game_date) {
    const score =
      latest.winner_score != null && latest.loser_score != null
        ? ` ${latest.winner_score}-${latest.loser_score}`
        : '';
    return `${owner} holds ${c.county_name}, ${c.state_name} — ${pop} · stolen from ${latest.loser_name}${score} on ${formatDate(latest.game_date)}`;
  }
  if (detail.is_home_turf) {
    return `${owner} holds home turf in ${c.county_name}, ${c.state_name} — ${pop} (never flipped this season)`;
  }
  return `${owner} holds ${c.county_name}, ${c.state_name} — ${pop}`;
}

const TerritoryDrawer: React.FC<Props> = ({
  leagueId,
  pick,
  ownership,
  popByFips,
  countyLabels,
  selectedDate,
  selectedSeason = null,
  soldiers = [],
  onClose,
}) => {
  const [detail, setDetail] = useState<CountyDetailResponse | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tidbits, setTidbits] = useState<TeamEmpireTidbits | null>(null);
  const [tidbitsLoading, setTidbitsLoading] = useState(false);

  const empire = useMemo(() => {
    if (pick.mode !== 'territory') return null;
    return buildEmpireStats(ownership, pick.team.id, popByFips, countyLabels);
  }, [pick, ownership, popByFips, countyLabels]);

  const conquestChain = useMemo(
    () => (detail ? buildConquestChain(detail) : null),
    [detail]
  );

  useEffect(() => {
    if (pick.mode !== 'territory') {
      setTidbits(null);
      return;
    }
    let cancelled = false;
    setTidbitsLoading(true);
    territoryService
      .getTeamTidbits(leagueId, pick.team.id)
      .then((t) => {
        if (!cancelled) setTidbits(t);
      })
      .catch(() => {
        if (!cancelled) setTidbits(null);
      })
      .finally(() => {
        if (!cancelled) setTidbitsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId, pick]);

  useEffect(() => {
    if (pick.mode !== 'county') {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    territoryService
      .getCountyDetail(leagueId, pick.fips, {
        date: selectedDate || undefined,
        season: selectedSeason ?? undefined,
      })
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e) => {
        if (!cancelled) setDetailError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId, pick, selectedDate, selectedSeason]);

  const copyBrag = async () => {
    if (!detail) return;
    const text = buildBrag(detail);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <aside className="om-drawer" role="dialog" aria-label="Territory details">
      <button type="button" className="om-drawer__x" aria-label="Close" onClick={onClose}>
        ×
      </button>

      {pick.mode === 'territory' && empire && (
        <div className="om-drawer__body">
          <p className="om-drawer__eyebrow">Empire</p>
          <h2 className="om-drawer__title">
            <span className="om-drawer__swatch" style={{ background: empire.team.color }} />
            {empire.team.name}
          </h2>
          <div className="om-drawer__stats">
            <div>
              <strong>{empire.counties.toLocaleString()}</strong>
              <span>counties · {pct(empire.countyShare)} of map</span>
            </div>
            <div>
              <strong>{formatCompactPop(empire.population)}</strong>
              <span>people · {pct(empire.popShare)} of map</span>
            </div>
            <div>
              <strong>#{empire.rankByCounties || '—'}</strong>
              <span>
                of {empire.empiresWithLand} by counties
                {empire.rankByPopulation ? ` · #${empire.rankByPopulation} by pop` : ''}
              </span>
            </div>
          </div>
          {empire.crownJewel && empire.crownJewel.population > 0 && (
            <p className="om-drawer__jewel">
              Crown jewel: <strong>{empire.crownJewel.name}</strong> (
              {formatCompactPop(empire.crownJewel.population)} people)
            </p>
          )}

          {(tidbitsLoading || tidbits) && (
            <div className="om-drawer__tidbits">
              <p className="om-drawer__eyebrow">All-time archive</p>
              {tidbitsLoading && !tidbits && (
                <p className="om-drawer__hint">Loading empire records…</p>
              )}
              {tidbits?.peak_counties && (
                <div className="om-drawer__tidbit">
                  <strong>{tidbits.peak_counties.counties.toLocaleString()}</strong>
                  <span>
                    most counties ever
                    {tidbits.peak_counties.season_year != null
                      ? ` · ${formatSeasonYear(leagueId, tidbits.peak_counties.season_year)}`
                      : ''}
                    {tidbits.peak_counties.date
                      ? ` · ${formatDate(tidbits.peak_counties.date)}`
                      : ''}
                  </span>
                </div>
              )}
              {tidbits?.biggest_loss && (
                <div className="om-drawer__tidbit om-drawer__tidbit--loss">
                  <strong>−{tidbits.biggest_loss.counties.toLocaleString()}</strong>
                  <span>
                    biggest loss
                    {tidbits.biggest_loss.foe_name
                      ? ` to ${foeLabel(tidbits.biggest_loss.foe_name, tidbits.biggest_loss.foe_short)}`
                      : ''}
                    {tidbits.biggest_loss.date ? ` · ${formatDate(tidbits.biggest_loss.date)}` : ''}
                  </span>
                </div>
              )}
              {tidbits?.biggest_win && (
                <div className="om-drawer__tidbit om-drawer__tidbit--win">
                  <strong>+{tidbits.biggest_win.counties.toLocaleString()}</strong>
                  <span>
                    biggest conquest
                    {tidbits.biggest_win.foe_name
                      ? ` from ${foeLabel(tidbits.biggest_win.foe_name, tidbits.biggest_win.foe_short)}`
                      : ''}
                    {tidbits.biggest_win.date ? ` · ${formatDate(tidbits.biggest_win.date)}` : ''}
                  </span>
                </div>
              )}
            </div>
          )}

          <p className="om-drawer__hint">Click a county for its war log</p>
          <div className="om-drawer__actions">
            <Link className="om-drawer__btn" to={`/enlist/army/${empire.team.id}`}>
              Army wall
            </Link>
            <Link className="om-drawer__btn om-drawer__btn--ghost" to="/enlist">
              Enlist
            </Link>
          </div>
        </div>
      )}

      {pick.mode === 'county' && (
        <div className="om-drawer__body">
          <p className="om-drawer__eyebrow">
            {pick.stateAbbr}
            {detail?.is_home_turf === true
              ? ' · Home turf'
              : detail?.is_home_turf === false
                ? ' · Occupied'
                : ''}
          </p>
          <h2 className="om-drawer__title">{pick.name}</h2>
          <div className="om-drawer__owner">
            <span className="om-drawer__swatch" style={{ background: pick.team.color }} />
            <div>
              <strong>{pick.team.name}</strong>
              <span>
                {pick.countyCount.toLocaleString()} counties in this empire
              </span>
            </div>
          </div>

          <div className="om-drawer__stats">
            <div>
              <strong>
                {detail?.county.population != null
                  ? detail.county.population.toLocaleString()
                  : formatCompactPop(
                      Number(
                        popByFips[String(pick.fips).padStart(5, '0')] ||
                          popByFips[pick.fips] ||
                          0
                      )
                    )}
              </strong>
              <span>population</span>
            </div>
            {detail?.county_share_of_owner_pop != null && (
              <div>
                <strong>{pct(detail.county_share_of_owner_pop)}</strong>
                <span>of {detail.current_owner?.short_name || pick.team.short}&apos;s people</span>
              </div>
            )}
            <div>
              <strong>{detail?.times_flipped ?? '—'}</strong>
              <span>flips this season</span>
            </div>
            <div>
              <strong>
                {detail?.days_held != null
                  ? detail.days_held
                  : detail?.times_flipped === 0
                    ? 'All'
                    : '—'}
              </strong>
              <span>
                {detail?.times_flipped === 0 && detail?.days_held == null
                  ? 'season (unflipped)'
                  : 'days held'}
              </span>
            </div>
          </div>

          {detailLoading && <p className="om-drawer__hint">Loading war log…</p>}
          {detailError && <p className="om-drawer__error">{detailError}</p>}

          {conquestChain && conquestChain.nodes.length > 0 && (
            <div className="om-drawer__chain">
              <p className="om-drawer__eyebrow">Conquest chain</p>
              <ol className="om-drawer__chain-row" aria-label="County ownership chain">
                {conquestChain.nodes.map((node, i) => {
                  const edge = i > 0 ? conquestChain.edges[i - 1] : null;
                  const isCurrent = i === conquestChain.nodes.length - 1;
                  return (
                    <React.Fragment key={`${node.teamId}-${i}`}>
                      {edge && (
                        <li className="om-drawer__chain-edge" aria-hidden>
                          <span className="om-drawer__chain-edge-mark">
                            <svg viewBox="0 0 28 12" width="28" height="12">
                              <path
                                d="M1 6h20M16 1.5l7 4.5-7 4.5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span className="om-drawer__chain-edge-meta">
                            {edge.date && <span className="om-drawer__chain-date">{shortMonthDay(edge.date)}</span>}
                            {edge.score && <span className="om-drawer__chain-score">{edge.score}</span>}
                          </span>
                        </li>
                      )}
                      <li className="om-drawer__chain-node">
                        <ChainLogo team={node} current={isCurrent} />
                        <span className="om-drawer__chain-caption">
                          {i === 0 ? 'Open' : isCurrent ? 'Now' : node.short}
                        </span>
                      </li>
                    </React.Fragment>
                  );
                })}
              </ol>
              {conquestChain.nodes.length === 1 && detail && (
                <p className="om-drawer__hint">
                  {detail.is_home_turf
                    ? 'Opening-map home turf — never flipped this season.'
                    : 'No flips recorded yet this season.'}
                </p>
              )}
            </div>
          )}

          {detail?.latest_conquest && (
            <div className="om-drawer__latest">
              <p className="om-drawer__eyebrow">How it was won</p>
              <p className="om-drawer__headline">
                <strong>{detail.latest_conquest.winner_name}</strong> defeated{' '}
                {detail.latest_conquest.loser_name}
                {detail.latest_conquest.winner_score != null &&
                detail.latest_conquest.loser_score != null
                  ? ` ${detail.latest_conquest.winner_score}-${detail.latest_conquest.loser_score}`
                  : ''}
              </p>
              {detail.latest_conquest.winner_top_player && (
                <p className="om-drawer__hint">
                  behind {detail.latest_conquest.winner_top_player}
                  {detail.latest_conquest.winner_top_points != null
                    ? `'s ${Number(detail.latest_conquest.winner_top_points).toFixed(1)}`
                    : ''}
                </p>
              )}
              <p className="om-drawer__hint">{formatDate(detail.latest_conquest.game_date)}</p>
            </div>
          )}

          {detail && detail.history.length > 0 && (
            <div className="om-drawer__log">
              <p className="om-drawer__eyebrow">Season war log</p>
              <ul>
                {[...detail.history].reverse().map((h) => (
                  <li key={h.conquest_id}>
                    <span className="om-drawer__log-date">{formatDate(h.game_date)}</span>
                    <span>
                      {h.winner_name || h.new_owner_name} took from{' '}
                      {h.loser_name || h.previous_owner_name || '—'}
                      {h.winner_score != null && h.loser_score != null
                        ? ` (${h.winner_score}-${h.loser_score})`
                        : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {soldiers.length > 0 && (
            <p className="om-drawer__hint">
              Stationed:{' '}
              {soldiers.slice(0, 6).map((s, i) => (
                <React.Fragment key={s.handle}>
                  {i > 0 ? ', ' : ''}
                  <Link to={`/s/${encodeURIComponent(s.handle)}`}>@{s.handle}</Link>
                </React.Fragment>
              ))}
              {soldiers.length > 6 ? ` +${soldiers.length - 6}` : ''}
            </p>
          )}

          <div className="om-drawer__actions">
            <button type="button" className="om-drawer__btn" onClick={copyBrag} disabled={!detail}>
              {copied ? 'Copied' : 'Copy brag'}
            </button>
            <Link className="om-drawer__btn om-drawer__btn--ghost" to="/enlist">
              Enlist here
            </Link>
          </div>
        </div>
      )}
    </aside>
  );
};

export default TerritoryDrawer;
